import { NavLink } from 'react-router-dom';
import {
  Building2,
  BookOpen,
  BookMarked,
  Table2,
  GitMerge,
  FileBarChart,
  Settings,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  LogOut,
  Calculator,
  Scale,
  AlertTriangle,
  TrendingUp,
  GitBranch,
  FileCode,
  Leaf,
  Globe,
  CheckCircle,
  Link,
  Store,
  ShieldCheck,
  Clock,
  ListChecks,
  Settings2,
  Wallet,
  Sparkles,
  Brain,
  FileSpreadsheet,
  ArrowRightLeft,
} from 'lucide-react';
import { useAuthStore, useAppStore } from '@/store';
import clsx from 'clsx';

// ─── Navigation Items ───────────────────────────────────────────────────────

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
  divider?: boolean;
  dividerLabel?: string;
  disabled?: boolean;
}

const navItems: NavItem[] = [
  { path: '/entities', label: 'Entities', icon: Building2 },
  { path: '/icp-partners', label: 'ICP Partners', icon: ArrowRightLeft },
  { path: '/entity-code-mapping', label: 'Entity Code Mapping', icon: Link },
  { path: '/chart-of-accounts', label: 'Chart of Accounts', icon: BookOpen },
  { path: '/entity-coa', label: 'Entity COA', icon: FileSpreadsheet },
  { path: '/trial-balances', label: 'Trial Balances', icon: Table2 },
  { path: '/intercompany', label: 'Intercompany (ICP)', icon: ArrowRightLeft },
  { path: '/ic-disputes', label: 'IC Disputes', icon: AlertTriangle },
  { path: '/financial-reports', label: 'Financial Reports', icon: BarChart3 },
  { path: '/financial-validation', label: 'Financial Validation', icon: ShieldCheck },
  { path: '/journal-entries', label: 'Journal Entries', icon: BookMarked },
  { path: '/consolidation', label: 'Consolidation', icon: GitMerge },
  { path: '/reports', label: 'Reports', icon: FileBarChart },
  // { path: '/audit', label: 'Audit Trail', icon: Shield },
  { path: '/data-quality', label: 'Data Quality', icon: ShieldCheck, divider: true, dividerLabel: 'Quality & Close', disabled: true },
  { path: '/predictive-close', label: 'Predictive Close', icon: Clock, disabled: true },
  { path: '/close-status', label: 'Close Status', icon: ListChecks, disabled: true },
  { path: '/ai-mapping', label: 'AI Mapping Review', icon: Sparkles, divider: true, dividerLabel: 'AI Intelligence', disabled: true },
  { path: '/nl-query', label: 'NL Query', icon: Brain, disabled: true },
  { path: '/forecasting', label: 'Forecasting', icon: TrendingUp, divider: true, dividerLabel: 'Intelligence', disabled: true },
  { path: '/scenarios', label: 'Scenario Modeling', icon: GitBranch, disabled: true },
  { path: '/anomalies', label: 'Anomaly Detection', icon: AlertTriangle, disabled: true },
  { path: '/kpi', label: 'KPI Dashboard', icon: BarChart3, divider: true, dividerLabel: 'Analytics', disabled: true },
  { path: '/tax', label: 'Tax Computation', icon: Calculator, divider: true, dividerLabel: 'Tax & Compliance', disabled: true },
  { path: '/transfer-pricing', label: 'Transfer Pricing', icon: Scale, disabled: true },
  { path: '/xbrl', label: 'XBRL Management', icon: FileCode, disabled: true },
  { path: '/esg', label: 'ESG Dashboard', icon: Leaf, disabled: true },
  { path: '/multi-gaap', label: 'Multi-GAAP', icon: Globe, disabled: true },
  { path: '/rules', label: 'Rules Engine', icon: Settings2, divider: true, dividerLabel: 'Configuration', disabled: true },
  { path: '/budget', label: 'Budget & Forecast', icon: Wallet, disabled: true },
  { path: '/approvals', label: 'Approval Workflows', icon: CheckCircle, divider: true, dividerLabel: 'Administration', disabled: true },
  { path: '/blockchain-audit', label: 'Blockchain Audit', icon: Link, disabled: true },
  { path: '/marketplace', label: 'Skill Marketplace', icon: Store, disabled: true },
  { path: '/settings', label: 'Settings', icon: Settings, divider: true, dividerLabel: '' },
];

// ─── Sidebar Component ──────────────────────────────────────────────────────

export default function Sidebar() {
  const { user, logout } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar } = useAppStore();

  return (
    <aside
      className={clsx(
        'fixed inset-y-0 left-0 z-40 flex flex-col bg-sidebar-bg transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo / Brand */}
      <div className="flex items-center h-14 px-4 border-b border-white/10">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
            <BarChart3 className="w-4.5 h-4.5 text-white" />
          </div>
          {!sidebarCollapsed && (
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-bold text-white tracking-tight truncate">
                ConsolidateAI
              </span>
              <span className="text-[10px] text-sidebar-text leading-tight">
                Financial Consolidation
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto scrollbar-thin">
        {navItems.map((item) => (
          <div key={item.path}>
            {/* Section divider */}
            {item.divider && (
              <div className={clsx('px-3', item.dividerLabel ? 'pt-4 pb-1' : 'pt-2 pb-1')}>
                {!sidebarCollapsed && item.dividerLabel ? (
                  <span className="text-[10px] font-bold text-sidebar-text/50 uppercase tracking-wider">
                    {item.dividerLabel}
                  </span>
                ) : (
                  <div className="border-t border-white/10" />
                )}
              </div>
            )}
            {item.disabled ? (
              <div
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium cursor-not-allowed opacity-30 relative"
                title={`${item.label} (coming soon)`}
              >
                <item.icon className="flex-shrink-0 w-[18px] h-[18px] text-sidebar-text/50" />
                {!sidebarCollapsed && (
                  <span className="truncate text-sidebar-text/50">{item.label}</span>
                )}
              </div>
            ) : (
              <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative',
                    isActive
                      ? 'bg-sidebar-active text-sidebar-text-active'
                      : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Active indicator */}
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-primary-400 rounded-r" />
                    )}
                    <item.icon
                      className={clsx(
                        'flex-shrink-0 w-[18px] h-[18px] transition-colors',
                        isActive ? 'text-primary-400' : 'text-sidebar-text group-hover:text-gray-300',
                      )}
                    />
                    {!sidebarCollapsed && (
                      <span className="truncate">{item.label}</span>
                    )}
                  </>
                )}
              </NavLink>
            )}
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="flex items-center justify-center h-10 mx-2 mb-2 rounded-lg text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active transition-colors"
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      {/* User section at bottom */}
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-700 flex items-center justify-center text-white text-xs font-semibold">
            {user?.full_name
              ?.split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2) || 'U'}
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-text-active truncate">
                {user?.full_name || 'User'}
              </p>
              <p className="text-xs text-sidebar-text capitalize truncate">
                {user?.role || 'viewer'}
              </p>
            </div>
          )}
          {!sidebarCollapsed && (
            <button
              onClick={logout}
              className="flex-shrink-0 p-1.5 text-sidebar-text hover:text-red-400 rounded-lg hover:bg-sidebar-hover transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
